/* eslint-disable jsx-a11y/no-autofocus */
'use client';

import { toast } from '@ahm/react-ui/components/toast';
import {
  Box,
  PinInput,
  Stack,
  Tabs,
  TabsPanel,
  Text,
  Title,
  rem,
} from '@mantine/core';
import { useEffect, useState } from 'react';
import { useResetPasscodeMutation } from '../api/use-reset-passcode';
import { useAuth } from '../auth-provider';
import { useSearchParams } from '../lib/navigation';
import { hidePhoneNumber } from '../lib/utils';
import { authLocales, createTranslations, type Locales } from '../locales';

export enum ResetPasscodeStep {
  NewPasscode = 'new-passcode',
  ReNewPasscode = 're-new-passcode',
}

type Props = {
  lang?: keyof Locales;
};
export default function ResetPasscodeForm({ lang = 'vi' }: Props) {
  const tAuth = createTranslations({ locales: authLocales, lang });

  const searchParams = useSearchParams();

  const [newPasscode, setNewPasscocde] = useState('');
  const [reNewPasscode, setReNewPasscocde] = useState('');
  const [tabResetPasscode, setTabResetPasscode] = useState(
    ResetPasscodeStep.NewPasscode
  );

  const { loginData } = useAuth();

  useEffect(() => {
    if (newPasscode) {
      setTabResetPasscode(ResetPasscodeStep.ReNewPasscode);
    } else {
      setTabResetPasscode(ResetPasscodeStep.NewPasscode);
    }
  }, [newPasscode]);

  const { mutate: resetPassCode, isPending } = useResetPasscodeMutation({});
  const onChangeInputComplete = (value: string) => {
    if (newPasscode !== value) {
      setReNewPasscocde(value);
    } else {
      const token = searchParams.get('token');
      if (!token) return;
      const body = { token, new_passcode: value };
      resetPassCode(body, {
        onSuccess: () => {
          window.location.reload();
        },
        onError: (e) => {
          const errorData = e?.response?.data;

          if (errorData) {
            toast.error({
              title: errorData?.title,
              message: errorData?.description,
              withIcon: true,
            });
          }
        },
      });
    }
  };

  return (
    <Box
      display="flex"
      w="100%"
      flex={1}
      component="div"
      pb="xs"
      style={{ flexDirection: 'column' }}
    >
      <Stack align="stretch" flex={1} gap="sm">
        <Title order={2}>{tAuth('enterPassword')}</Title>
        <Text size="md" c="neutral.6" fw={500}>
          {!newPasscode ? tAuth('enterNewPassword') : tAuth('reEnterPassword')}
          <Text
            component="span"
            size="md"
            fw={700}
            c="neutral.7"
          >{`+${hidePhoneNumber(loginData.id)}`}</Text>
        </Text>
        <Stack gap={0} w="100%" mt="sm">
          <Tabs value={tabResetPasscode}>
            <TabsPanel value={ResetPasscodeStep.NewPasscode}>
              <PinInput
                // ref={focusTrapRef}
                autoFocus
                length={6}
                gap={rem(8)}
                type="number"
                inputType="tel"
                inputMode="numeric"
                size="lg"
                placeholder=""
                styles={{
                  root: {
                    '--mantine-color-gray-4': 'var(--mantine-color-neutral-0)',
                  },
                  input: {
                    backgroundColor: 'var(--mantine-color-neutral-0)',
                  },
                }}
                onComplete={setNewPasscocde}
              />
            </TabsPanel>
            <TabsPanel value={ResetPasscodeStep.ReNewPasscode}>
              <PinInput
                disabled={isPending}
                autoFocus
                // ref={focusTrapRef}
                length={6}
                gap={rem(8)}
                type="number"
                inputType="tel"
                inputMode="numeric"
                size="lg"
                placeholder=""
                styles={{
                  root: {
                    '--mantine-color-gray-4': 'var(--mantine-color-neutral-0)',
                  },
                  input: {
                    backgroundColor: 'var(--mantine-color-neutral-0)',
                  },
                }}
                onComplete={onChangeInputComplete}
              />
            </TabsPanel>
          </Tabs>

          <Text fw={500} size="sm" c="red.5" my={rem(8)} mih={rem(20)}>
            {tabResetPasscode === ResetPasscodeStep.ReNewPasscode &&
              newPasscode &&
              reNewPasscode &&
              newPasscode !== reNewPasscode &&
              tAuth('rePasswordNotMatch')}
          </Text>
        </Stack>
      </Stack>
    </Box>
  );
}
